import { Grid, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import {
  NON_ROUTABLE_REASON_LABEL,
  NON_ROUTABLE_SOURCE,
  NON_ROUTABLE_SOURCE_LABEL,
  RELAY_ERROR_LABEL,
  labelOf,
} from '../constants/nonRoutableRequests';
import { formatParkedAt } from '../utils/nonRoutableRequestDates';

const Field = ({ label, value }) => (
  <Grid item xs={6}>
    <Typography variant='caption' color='textSecondary' display='block'>
      {label}
    </Typography>
    <Typography variant='body2' sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
      {value ?? 'N/A'}
    </Typography>
  </Grid>
);

Field.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
};

Field.defaultProps = { value: undefined };

/**
 * Identifies the request an irreversible action is about to be taken on.
 *
 * `referenceId` leads, because it is the partner's own handle for the prescription and the only
 * thing an operator can cross-check against anything outside this screen.
 *
 * NO REQUEST CONTENTS APPEAR HERE, and none may. The parked payload — shipping address, SIG,
 * prescriber and patient identifiers — is classified RESTRICTED and the backend deliberately
 * projects none of it. Nothing on this panel is derived from it.
 *
 * POM-6279: the Source, and on a parked relay fax where it was going and why it failed. The
 * destination is a pharmacy's business name, not PHI.
 */
const NonRoutableRequestIdentity = ({ item }) => (
  <Grid container spacing={1.5} sx={{ mb: 1 }}>
    <Field label='Reference Id' value={item.referenceId} />
    <Field label='Partner Id' value={item.partnerId} />
    <Field label='Patient Id' value={item.patientId} />
    <Field label='Reason' value={labelOf(NON_ROUTABLE_REASON_LABEL, item.nonRoutableReason)} />
    <Field label='Source' value={labelOf(NON_ROUTABLE_SOURCE_LABEL, item.source)} />
    <Field label='Parked' value={formatParkedAt(item.parkedAt)} />
    <Field label='Line items' value={item.nonRoutableLineCount} />
    {item.source === NON_ROUTABLE_SOURCE.RELAY_FAX && (
      <>
        <Field label='Destination pharmacy' value={item.destinationName} />
        <Field label='Fax problem' value={labelOf(RELAY_ERROR_LABEL, item.relayErrorCode)} />
      </>
    )}
  </Grid>
);

NonRoutableRequestIdentity.propTypes = {
  item: PropTypes.shape({
    referenceId: PropTypes.string,
    partnerId: PropTypes.string,
    patientId: PropTypes.string,
    nonRoutableReason: PropTypes.string,
    parkedAt: PropTypes.string,
    nonRoutableLineCount: PropTypes.number,
    source: PropTypes.string,
    destinationName: PropTypes.string,
    relayErrorCode: PropTypes.string,
  }).isRequired,
};

export default NonRoutableRequestIdentity;
